import { useEffect, useMemo, useState } from 'react'
import { DateRangePicker } from '../Table/component/Table'
import { ApexColumnChart, ApexLineChart, ApexPieChart } from './component/Charts'
import ReportAnalytics from './component/Dashboard/ReportAnalytics'
import MultiSelectDropdown from './MultiSelectDropdown'
import { useLocation, useNavigate } from 'react-router-dom'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import axios from 'axios'
import BaseYearModal from './component/Settings/BaseYearModal'

const PIE_COLORS = [
  '#4F46E5', // indigo
  '#06B6D4', // cyan
  '#22C55E', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#A855F7', // purple
  '#14B8A6', // teal
  '#F97316', // orange
]

const makeLabelColorMap = (labels = []) => {
  const map = {}
  labels.forEach((lbl, idx) => {
    map[lbl] = PIE_COLORS[idx % PIE_COLORS.length]
  })
  return map
}

const Analytics = () => {
  const [dateRange, setDateRange] = useState({ start: '', end: '' })
  const navigate = useNavigate()
  const location = useLocation()
  const axiosPrivate = useAxiosPrivate()

  const [isLoading, setIsLoading] = useState(false)
  const [active, setActive] = useState('Company')
  const buttons = ['Company', 'Sites', 'Scope', 'Drill Down', 'Report']

  const [totalEmission, setTotalEmission] = useState({})
  const [rawRecords, setRawRecords] = useState([])
  const [sitesList, setSitesList] = useState([])

  // Filters
  const [selectedSites, setSelectedSites] = useState(['All Sites'])
  const [selectedScopes, setSelectedScopes] = useState(['Scope 1', 'Scope 2', 'Scope 3'])
  const [selectedDrill, setSelectedDrill] = useState('')

  // Modal drilldown from Scope chart
  const [showDrillModal, setShowDrillModal] = useState(false)
  const [modalDrillKey, setModalDrillKey] = useState('')
  const [modalMonthIndex, setModalMonthIndex] = useState(null)

  // Base Year & YoY comparison states
  const [showBaseYearModal, setShowBaseYearModal] = useState(false)
  const [currentYearTotals, setCurrentYearTotals] = useState(null)
  const [baseYearTotals, setBaseYearTotals] = useState(null)

  const scopeMap = useMemo(
    () => ({
      1: 'Scope 1',
      2: 'Scope 2',
      3: 'Scope 3',
    }),
    []
  )

  useEffect(() => {
    let isMounted = true;
    const fetchSites = async () => {
      try {
        const response = await axiosPrivate.get('/sites');
        if (isMounted) setSitesList(response.data);
      } catch (error) {
        console.error("Error fetching sites:", error);
      }
    };
    fetchSites();
    return () => { isMounted = false; };
  }, [axiosPrivate]);

  const activeOptions = useMemo(
    () => ({
      Sites: { siteOptions: ['All Sites', ...sitesList.map(s => s.name)] },
      Scope: { scopeOptions: ['Scope 1', 'Scope 2', 'Scope 3'] },
      'Drill Down': { drillDownOptions: [] },
    }),
    [sitesList]
  )


  // =========================
  // Helpers
  // =========================
  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })

  const buildBreakdownRows = (labels = [], values = []) => {
    const total = values.reduce((a, b) => a + Number(b || 0), 0)

    return labels
      .map((label, i) => {
        const value = Number(values[i] || 0)
        const pct = total > 0 ? (value / total) * 100 : 0
        return { label, value, pct }
      })
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value)
  }

  // =========================
  // Fetch analytics raw records
  // =========================
  useEffect(() => {
    let isMounted = true
    const controller = new AbortController()

    const fetchAnalytics = async () => {
      try {
        setIsLoading(true)

        const res = await axiosPrivate.get('company/analytics/company-scope', {
          signal: controller.signal,
        })

        const normalized = (Array.isArray(res.data) ? res.data : []).map((row) => {
          const d = row?.emissionDate ? new Date(row.emissionDate) : null
          const monthIndex = d ? d.getMonth() : null

          return {
            ...row,
            site: row.site || 'Site 1', // TEMP: until DB has sites
            emission: Number(row?.emission || 0),
            scopeLabel: scopeMap[row?.scope] || `Scope ${row?.scope ?? ''}`,
            monthIndex,
          }
        })

        if (isMounted) setRawRecords(normalized)
      } catch (err) {
        if (axios.isCancel(err)) return
        console.error('Analytics fetch failed:', err)

        if (err.response && err.response.status === 403) {
          navigate('/sign-in', { state: { from: location }, replace: true })
        }
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    fetchAnalytics()

    return () => {
      isMounted = false
      controller.abort()
    }
  }, [axiosPrivate, navigate, location, scopeMap])

  // =========================
  // Derived: categories
  // =========================
  const categoryOptions = useMemo(() => {
    const set = new Set([
      'Stationary Combustion',
      'Mobile Combustion',
      'Fugitive Emission',
      'Process Emission',
      'Energy Purchase',
      'Electric Vehicle',
    ])
    rawRecords.forEach((r) => {
      if (r?.category) set.add(r.category)
    })
    return Array.from(set)
  }, [rawRecords])

  useEffect(() => {
    if (!selectedDrill && categoryOptions.length > 0) setSelectedDrill(categoryOptions[0])
  }, [categoryOptions, selectedDrill])

  // =========================
  // Apply filters (date + sites)
  // =========================
  const resolveBucket = (siteId, siteSet, hasAllSites, sitesList) => {
    let current = sitesList.find(s => s.id === siteId);
    if (!current) return 'Unknown Site';

    if (hasAllSites) {
      // Walk to top-level
      while (current.parentSiteId) {
        const parent = sitesList.find(s => s.id === current.parentSiteId);
        if (!parent) break;
        current = parent;
      }
      return current.name;
    } else {
      // Walk up until we hit a selected site
      while (current) {
        if (siteSet.has(current.name)) return current.name;
        if (!current.parentSiteId) break;
        current = sitesList.find(s => s.id === current.parentSiteId);
      }
      return null;
    }
  }

  const processedRecords = useMemo(() => {
    let rows = rawRecords

    // date filter
    if (dateRange?.start || dateRange?.end) {
      const start = dateRange?.start ? new Date(dateRange.start) : null
      const end = dateRange?.end ? new Date(dateRange.end) : null

      rows = rows.filter((r) => {
        const d = r?.emissionDate ? new Date(r.emissionDate) : null
        if (!d) return false
        if (start && d < start) return false
        if (end && d > end) return false
        return true
      })
    }

    const siteSet = new Set(selectedSites || [])
    const hasAllSites = siteSet.has('All Sites')

    return rows.map(r => {
      const bucketName = resolveBucket(r.siteId, siteSet, hasAllSites, sitesList);
      return { ...r, bucketName };
    }).filter(r => r.bucketName !== null);
  }, [rawRecords, dateRange, selectedSites, sitesList])

  // =========================
  // Sites tab series
  // X-axis: Months, Lines: Scope 1/2/3
  // =========================
  const sitesScopeSeries = useMemo(() => {
    const selectedSet = new Set(selectedScopes || [])
    const map = new Map() // key: `${scopeLabel}-${monthIndex}` -> sum

    processedRecords.forEach((r) => {
      if (r.monthIndex == null) return
      if (!selectedSet.has(r.scopeLabel)) return

      const key = `${r.scopeLabel}-${r.monthIndex}`
      map.set(key, (map.get(key) || 0) + r.emission)
    })

    const orderedScopes = ['Scope 1', 'Scope 2', 'Scope 3'].filter((s) => selectedSet.has(s))

    if (orderedScopes.length === 0) {
      return [{ name: 'Scope 1', data: Array(12).fill(0) }]
    }

    return orderedScopes.map((scopeLabel) => ({
      name: scopeLabel,
      data: Array.from({ length: 12 }, (_, m) => map.get(`${scopeLabel}-${m}`) || 0),
    }))
  }, [processedRecords, selectedScopes])


  // =========================
  // Chart click handler
  // =========================
  const handleScopeClick = (category, monthIndex) => {
    if (category == null || monthIndex == null) return
    setModalDrillKey(category)
    setModalMonthIndex(monthIndex)
    setShowDrillModal(true)
  }

  // =========================
  // Series: Company = by Site (line)
  // =========================
  const companySeries = useMemo(() => {
    const map = new Map()

    processedRecords.forEach((r) => {
      if (r.monthIndex == null) return
      const key = `${r.bucketName}-${r.monthIndex}`
      map.set(key, (map.get(key) || 0) + r.emission)
    })

    const buckets = Array.from(new Set(processedRecords.map((r) => r.bucketName))).filter(Boolean)

    const series = buckets.map((bucket) => ({
      name: bucket,
      data: Array.from({ length: 12 }, (_, m) => map.get(`${bucket}-${m}`) || 0),
    }))

    return series.length > 0 ? series : [{ name: 'No Sites Found', data: Array(12).fill(0) }]
  }, [processedRecords])

  // =========================
  // Series: Scope tab = by Category (bar), filtered by selectedScopes
  // =========================
  const scopeCategorySeries = useMemo(() => {
    const selectedSet = new Set(selectedScopes || [])
    const map = new Map()

    processedRecords.forEach((r) => {
      if (!r.category || r.monthIndex == null) return
      if (!selectedSet.has(r.scopeLabel)) return

      const key = `${r.category}-${r.monthIndex}`
      map.set(key, (map.get(key) || 0) + r.emission)
    })

    const categories = Array.from(
      new Set(
        processedRecords
          .filter((r) => selectedSet.has(r.scopeLabel))
          .map((r) => r.category)
      )
    ).filter(Boolean)

    return categories.map((cat) => ({
      name: cat,
      data: Array.from({ length: 12 }, (_, m) => map.get(`${cat}-${m}`) || 0),
    }))
  }, [processedRecords, selectedScopes])

  // =========================
  // Pies: modal + drilldown
  // =========================
  const modalPie = useMemo(() => {
    if (!modalDrillKey || modalMonthIndex == null) return { labels: [], values: [] }

    const agg = {}
    processedRecords
      .filter((r) => r.category === modalDrillKey && r.monthIndex === modalMonthIndex)
      .forEach((r) => {
        const fuel = r.fuel || 'Unknown'
        agg[fuel] = (agg[fuel] || 0) + r.emission
      })

    return { labels: Object.keys(agg), values: Object.values(agg) }
  }, [processedRecords, modalDrillKey, modalMonthIndex])

  const drillDownPie = useMemo(() => {
    if (!selectedDrill) return { labels: [], values: [] }

    const agg = {}
    processedRecords
      .filter((r) => r.category === selectedDrill)
      .forEach((r) => {
        const fuel = r.fuel || 'Unknown'
        agg[fuel] = (agg[fuel] || 0) + r.emission
      })

    return { labels: Object.keys(agg), values: Object.values(agg) }
  }, [processedRecords, selectedDrill])

  // Color maps for dots (match donut order)
  const drillColorMap = useMemo(() => makeLabelColorMap(drillDownPie.labels), [drillDownPie.labels])
  const modalColorMap = useMemo(() => makeLabelColorMap(modalPie.labels), [modalPie.labels])

  // Breakdown rows
  const drillRows = useMemo(
    () => buildBreakdownRows(drillDownPie.labels, drillDownPie.values),
    [drillDownPie.labels, drillDownPie.values]
  )
  const drillTotal = useMemo(
    () => drillDownPie.values.reduce((a, b) => a + Number(b || 0), 0),
    [drillDownPie.values]
  )

  const modalRows = useMemo(() => buildBreakdownRows(modalPie.labels, modalPie.values), [modalPie.labels, modalPie.values])
  const modalTotal = useMemo(() => modalPie.values.reduce((a, b) => a + Number(b || 0), 0), [modalPie.values])

  // =========================
  // Header copy
  // =========================
  const headerCopy = useMemo(() => {
    switch (active) {
      case 'Company':
        return { title: 'GHG Emissions by Site', desc: 'Monthly emissions across all sites (tCO₂e)' }

      case 'Scope':
        return { title: 'GHG Emissions by Category', desc: 'Monthly emissions by category (tCO₂e)' }

      case 'Drill Down':
        return {
          title: selectedDrill || 'Fuel Breakdown',
          desc: 'Whole-year breakdown for selected category (tCO₂e)',
        }

      case 'Sites':
        return { title: 'GHG Emissions by Site', desc: 'Requires site data from database.' }

      default:
        return { title: 'Analytics', desc: '' }
    }
  }, [active, selectedDrill])


  // =========================
  // Render chart section
  // =========================
  const renderChart = () => {
    if (active !== 'Report' && isLoading) {
      return <div className="p-6 text-sm text-gray-500">Loading analytics...</div>
    }

    if (active !== 'Report' && !isLoading && processedRecords.length === 0) {
      return <div className="p-6 text-sm text-gray-500">No analytics records found for this company (yet).</div>
    }

    switch (active) {
      case 'Company':
        return <ApexLineChart data={companySeries} title={headerCopy.title} height={420} />

      case 'Sites':
        return (
          <ApexColumnChart
            data={sitesScopeSeries}
            title="GHG Emissions by Scope"
            height={420}
          />
        )

      case 'Scope':
        return (
          <ApexColumnChart
            data={scopeCategorySeries}
            title={headerCopy.title}
            height={420}
            onDataPointSelection={(category, monthIndex) => handleScopeClick(category, monthIndex)}
          />
        )

      case 'Drill Down':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4">
            {/* LEFT: Donut */}
            <div className="bg-white dark:bg-darkbg4 rounded-xl border p-4">
              <ApexPieChart
                data={drillDownPie.values}
                labels={drillDownPie.labels}
                title="Emission Breakdown (Tonnes)"
                height={360}
                colors={PIE_COLORS}
              />
            </div>

            {/* RIGHT: Details */}
            <div className="bg-white dark:bg-darkbg4 rounded-xl border p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-semibold dark:text-white">Emission Sources</p>
                  <p className="text-xs text-gray-500">Detailed breakdown for {selectedDrill || 'selected category'}</p>
                </div>
                <div className="text-sm font-semibold">
                  Total: {fmt(drillTotal)} <span className="text-xs font-normal text-gray-500">tCO₂e</span>
                </div>
              </div>

              <div className="divide-y dark:divide-gray-700">
                {drillRows.length === 0 ? (
                  <div className="py-6 text-sm text-gray-500">No breakdown data yet.</div>
                ) : (
                  drillRows.map((r) => (
                    <div key={r.label} className="py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span
                          className="w-2 h-2 rounded-full inline-block"
                          style={{ backgroundColor: drillColorMap[r.label] || '#9CA3AF' }}
                        />
                        <span className="text-sm font-medium dark:text-white">{r.label}</span>
                      </div>

                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {fmt(r.value)} <span className="text-xs">tCO₂e</span>
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-darkbg3 text-gray-700 dark:text-gray-200">
                          {r.pct.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )

      case 'Report':
        return <ReportAnalytics />

      default:
        return null
    }
  }

  // =========================
  // Total emission fetch
  // =========================
  useEffect(() => {
    let isMounted = true
    const controller = new AbortController()

    const fetchData = async () => {
      try {
        const totalEmissionResponse = await axiosPrivate.get('totalEmission/total-emission-amount', {
          signal: controller.signal,
        })
        if (isMounted) setTotalEmission(totalEmissionResponse.data)
      } catch (err) {
        if (axios.isCancel(err)) return
        console.error('Request failed:', err)

        if (err.response && err.response.status === 403) {
          navigate('/sign-in', { state: { from: location }, replace: true })
        }
      }
    }

    fetchData()

    return () => {
      isMounted = false
      controller.abort()
    }
  }, [navigate, location, axiosPrivate])

  useEffect(() => {
    let isMounted = true
    const controller = new AbortController()

    const fetchTotals = async () => {
      try {
        const selectedYear = dateRange?.start ? new Date(dateRange.start).getFullYear() : new Date().getFullYear()
        const baseYearType = localStorage.getItem('baseYearType') || 'rolling'
        const fixedBaseYear = Number(localStorage.getItem('fixedBaseYear')) || (selectedYear - 1)
        const baseYear = baseYearType === 'rolling' ? (selectedYear - 1) : fixedBaseYear

        const currentRes = await axiosPrivate.get(`/dashboards/total-emissions/${selectedYear}`, {
          signal: controller.signal,
        })

        let baseRes = null
        if (baseYear === selectedYear) {
          baseRes = currentRes
        } else {
          try {
            baseRes = await axiosPrivate.get(`/dashboards/total-emissions/${baseYear}`, {
              signal: controller.signal,
            })
          } catch (e) {
            console.warn('Base year emissions not found:', baseYear)
          }
        }

        if (isMounted) {
          if (currentRes?.data) setCurrentYearTotals(currentRes.data)
          if (baseRes?.data) setBaseYearTotals(baseRes.data)
        }
      } catch (err) {
        if (!axios.isCancel(err)) console.error('Failed fetching year totals:', err)
      }
    }

    fetchTotals()

    return () => {
      isMounted = false
      controller.abort()
    }
  }, [axiosPrivate, dateRange, showBaseYearModal])

  const getYoYStats = (currentVal, baseVal) => {
    const current = Number(currentVal || 0)
    const base = Number(baseVal || 0)

    const selectedYear = dateRange?.start ? new Date(dateRange.start).getFullYear() : new Date().getFullYear()
    const baseYearType = localStorage.getItem('baseYearType') || 'rolling'
    const fixedBaseYear = Number(localStorage.getItem('fixedBaseYear')) || (selectedYear - 1)
    const baseYear = baseYearType === 'rolling' ? (selectedYear - 1) : fixedBaseYear
    const yearLabel = baseYearType === 'fixed' ? `base year (${baseYear})` : `last year (${baseYear})`

    if (base === 0) {
      if (current > 0) return { text: `↑ 100% vs ${yearLabel}`, isIncrease: true }
      return { text: `0.0% vs ${yearLabel}`, isIncrease: false }
    }

    const diff = current - base
    const pct = (diff / base) * 100
    const absPct = Math.abs(pct).toFixed(1)

    if (pct > 0) {
      return { text: `↑ ${absPct}% vs ${yearLabel}`, isIncrease: true }
    } else if (pct < 0) {
      return { text: `↓ ${absPct}% vs ${yearLabel}`, isIncrease: false }
    } else {
      return { text: `0.0% vs ${yearLabel}`, isIncrease: false }
    }
  }

  const yoyTotal = getYoYStats(
    currentYearTotals?.grandTotal ?? totalEmission.total,
    baseYearTotals?.grandTotal
  )
  const yoyScope1 = getYoYStats(
    currentYearTotals?.totalScope1 ?? totalEmission.scope1,
    baseYearTotals?.totalScope1
  )
  const yoyScope2 = getYoYStats(
    currentYearTotals?.totalScope2 ?? totalEmission.scope2,
    baseYearTotals?.totalScope2
  )
  const yoyScope3 = getYoYStats(
    currentYearTotals?.totalScope3 ?? totalEmission.scope3,
    baseYearTotals?.totalScope3
  )

  return (
    <div className="flex flex-col w-full h-full">
      {/* FILTERS ROW */}
      <div className="flex items-center gap-3 flex-wrap px-4 py-3">
        {(active === 'Company' || active === 'Sites' || active === 'Scope' || active === 'Drill Down') && (
          <MultiSelectDropdown
            options={activeOptions.Sites.siteOptions}
            selected={selectedSites}
            onChange={setSelectedSites}
            label="Sites"
          />
        )}

        {(active === 'Company' || active === 'Scope' || active === 'Drill Down') && (
          <MultiSelectDropdown
            options={activeOptions.Scope.scopeOptions}
            selected={selectedScopes}
            onChange={setSelectedScopes}
            label="Scopes"
          />
        )}

        {active === 'Drill Down' && (
          <select
            value={selectedDrill}
            onChange={(e) => setSelectedDrill(e.target.value)}
            className="mt-3 px-3 py-2 rounded-md border bg-white dark:bg-darkbg4 dark:border-gray-600 text-sm"
          >
            <option value="">Select Category</option>
            {categoryOptions.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        )}

        <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} />

        <button
          onClick={() => setShowBaseYearModal(true)}
          className="mt-3 px-3 py-2 border rounded-md text-sm bg-white dark:bg-darkbg4 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
        >
          Compare YoY
        </button>
      </div>

      {/* KPI SUMMARY */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-4 py-4">
        <div className="bg-white dark:bg-darkbg4 rounded-lg border p-4">
          <p className="text-sm text-gray-500">Total Emissions</p>
          <p className="text-2xl font-bold mt-1">
            {fmt(currentYearTotals?.grandTotal ?? totalEmission.total)}
            <span className="text-sm font-normal ml-1">tCO₂e</span>
          </p>
          <p className={`text-xs mt-1 font-medium ${yoyTotal.isIncrease ? 'text-red-600' : 'text-green-600'}`}>
            {yoyTotal.text}
          </p>
        </div>

        <div className="bg-white dark:bg-darkbg4 rounded-lg border p-4">
          <p className="text-sm text-gray-500">Scope 1</p>
          <p className="text-2xl font-bold mt-1">
            {fmt(currentYearTotals?.totalScope1 ?? totalEmission.scope1)}
            <span className="text-sm font-normal ml-1">tCO₂e</span>
          </p>
          <p className={`text-xs mt-1 font-medium ${yoyScope1.isIncrease ? 'text-red-600' : 'text-green-600'}`}>
            {yoyScope1.text}
          </p>
        </div>

        <div className="bg-white dark:bg-darkbg4 rounded-lg border p-4">
          <p className="text-sm text-gray-500">Scope 2</p>
          <p className="text-2xl font-bold mt-1">
            {fmt(currentYearTotals?.totalScope2 ?? totalEmission.scope2)}
            <span className="text-sm font-normal ml-1">tCO₂e</span>
          </p>
          <p className={`text-xs mt-1 font-medium ${yoyScope2.isIncrease ? 'text-red-600' : 'text-green-600'}`}>
            {yoyScope2.text}
          </p>
        </div>

        <div className="bg-white dark:bg-darkbg4 rounded-lg border p-4">
          <p className="text-sm text-gray-500">Scope 3</p>
          <p className="text-2xl font-bold mt-1">
            {fmt(currentYearTotals?.totalScope3 ?? totalEmission.scope3)}
            <span className="text-sm font-normal ml-1">tCO₂e</span>
          </p>
          <p className={`text-xs mt-1 font-medium ${yoyScope3.isIncrease ? 'text-red-600' : 'text-green-600'}`}>
            {yoyScope3.text}
          </p>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="px-4 pb-2 text-sm text-gray-500">
        Company <span className="mx-2">›</span> <span className="font-medium text-gray-700">{active}</span>
      </div>

      {/* MAIN CARD */}
      <div className="mx-4 mb-6 bg-white dark:bg-darkbg4 rounded-xl shadow-sm border">
        <div className="px-6 pt-5 pb-3 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">{headerCopy.title}</h2>
            <p className="text-sm text-gray-500">{headerCopy.desc}</p>
          </div>

          <div className="flex gap-1 bg-gray-100 dark:bg-darkbg3 p-1 rounded-lg h-fit">
            {buttons.map((btn) => (
              <button
                key={btn}
                onClick={() => setActive(btn)}
                className={`px-4 py-2 text-sm rounded-md transition ${active === btn
                    ? 'bg-white dark:bg-darkbg4 shadow font-semibold'
                    : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                {btn}
              </button>
            ))}
          </div>
        </div>

        <div className="px-4 pb-6">{renderChart()}</div>
      </div>

      {/* MODAL DRILL DOWN */}
      {showDrillModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowDrillModal(false)}
        >
          <div
            className="bg-white dark:bg-darkbg4 p-6 rounded-xl shadow-2xl w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4 gap-4">
              <div>
                <h3 className="text-lg font-bold dark:text-white">{modalDrillKey}</h3>
                <p className="text-sm text-gray-500">Emission Breakdown (tCO₂e)</p>
              </div>
              <button
                onClick={() => setShowDrillModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* LEFT donut */}
              <div className="bg-white dark:bg-darkbg4 rounded-xl border p-4">
                <ApexPieChart data={modalPie.values} labels={modalPie.labels} height={320} colors={PIE_COLORS} />
              </div>

              {/* RIGHT details */}
              <div className="bg-white dark:bg-darkbg4 rounded-xl border p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold dark:text-white">Emission Sources</p>
                    <p className="text-xs text-gray-500">Detailed breakdown for {modalDrillKey}</p>
                  </div>
                  <div className="text-sm font-semibold">
                    Total: {fmt(modalTotal)} <span className="text-xs font-normal text-gray-500">tCO₂e</span>
                  </div>
                </div>

                <div className="divide-y dark:divide-gray-700">
                  {modalRows.length === 0 ? (
                    <div className="py-6 text-sm text-gray-500">No breakdown data for this month.</div>
                  ) : (
                    modalRows.map((r) => (
                      <div key={r.label} className="py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span
                            className="w-2 h-2 rounded-full inline-block"
                            style={{ backgroundColor: modalColorMap[r.label] || '#9CA3AF' }}
                          />
                          <span className="text-sm font-medium dark:text-white">{r.label}</span>
                        </div>

                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            {fmt(r.value)} <span className="text-xs">tCO₂e</span>
                          </span>
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-darkbg3 text-gray-700 dark:text-gray-200">
                            {r.pct.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showBaseYearModal && <BaseYearModal closeModal={() => setShowBaseYearModal(false)} />}
    </div>
  )
}

export default Analytics
